<template>
  <PivotTable
    ref="pivotTableRef"
    v-model="config"
    :loading
    :fields
    :readonly
    @click:value="handleClickValue"
    @click:text="handleClickText"
  >
    <template #total="attrs">
      <slot name="total" v-bind="attrs" />
    </template>
    <template #header="attrs">
      <slot name="header" v-bind="attrs" />
    </template>
    <template v-if="isDropdownConfig" #sidebar>
      <ConfigsDropdown
        :configs-list
        :config-index
        :default-config
        @update:storage="handleUpdateStorage"
      />
    </template>
    <template v-if="isTabsConfig" #bottom>
      <ConfigsTabs
        :configs-list
        :config-index
        :default-config
        @pin="handlePin"
        @update:storage="handleUpdateStorage"
      />
    </template>
    <template v-if="$slots.filter" #filter="slotProps">
      <slot name="filter" v-bind="slotProps" />
    </template>
  </PivotTable>
</template>

<script setup lang="ts">
import { computed, provide, ref, useTemplateRef, watch } from 'vue'
import { cloneDeep, isEqual } from 'lodash'

import {
  PivotTableClickValueData,
  PivotTableConfig,
  PivotTableFields,
  PivotTableStorage,
} from './utils/types'
import { PivotTableLayout } from './utils/enums'

import { DEFAULT_CONFIG_NAME } from './utils/const'

import { compareConfigsFields, prepareInputFields } from './utils/helpers'

import PivotTable from './PivotTable.vue'
import ConfigsDropdown from './components/ConfigsDropdown.vue'
import ConfigsTabs from './components/ConfigsTabs.vue'

type Props = {
  fields: PivotTableFields
  storage?: PivotTableStorage
  configType?: 'dropdown' | 'tabs'
  debounceDelay?: number
  readonly?: boolean
  presetConfig?: Partial<PivotTableConfig>
}

type Emits = {
  'update:storage': [data: PivotTableStorage]
  'fetch:data': [data: PivotTableConfig, callback: () => void]
  'click:value': [data: PivotTableClickValueData, amount: number]
  'click:text': [data: string]
  'pin': [data: PivotTableConfig, callback: () => void, index?: number]
}

const { configType = 'tabs', fields, storage, readonly, presetConfig = {} } = defineProps<Props>()
const emit = defineEmits<Emits>()

defineExpose({
  getDefaultConfig() {
    return defaultConfig.value
  },
  getExportElement: (): HTMLElement | null =>
    pivotTableRef.value?.getExportElement() ?? null,
})

const pivotTableRef = useTemplateRef('pivotTableRef')

provide('size', 'sm')

const loading = ref(true)
const isStorageUdpated = ref(true)

const defaultConfig = computed(() => ({
  name: DEFAULT_CONFIG_NAME,
  open: true,
  layout: PivotTableLayout.MODERN,
  fields: prepareInputFields(fields),
  filter: undefined,
  sortColumns: [],
  sortRows: [],
  ...presetConfig
}))

const configsList = computed(() => {
  return storage?.configs || [defaultConfig.value]
})
const configIndex = computed(() => storage?.index || 0)

const config = computed({
  get() {
    const data = cloneDeep(configsList.value[configIndex.value])
    if (!data) return defaultConfig.value
    return compareConfigsFields(data, fields, presetConfig)
  },
  set(data) {
    if (!storage?.configs) {
      handleUpdateStorage({
        configs: [data],
        index: 0,
      })
    } else {
      const configs = configsList.value.map((config, index) => index === configIndex.value ? data : config)
      handleUpdateStorage({
        configs,
        index: configIndex.value,
      })
    }
  },
})

const isDropdownConfig = computed(() => configType === 'dropdown' && !readonly)
const isTabsConfig = computed(() => configType === 'tabs' && !readonly)

const handleUpdateStorage = (data: PivotTableStorage) => {
  isStorageUdpated.value = true
  emit('update:storage', data)
}

const handlePin = async (data: PivotTableConfig, callback: () => void, index?: number) => {
  emit('pin', data, callback, index)
}

const isConfigChanged = (data: PivotTableConfig, prev?: PivotTableConfig) => {
  return !prev || !isEqual(data.fields, prev.fields)
}

const fetchData = (params: PivotTableConfig) => {
  emit('fetch:data', params, () => {
    loading.value = false
    pivotTableRef.value?.refresh()
  })
}

const handleClickValue = (data: PivotTableClickValueData, amount: number) => {
  emit('click:value', data, amount)
}

const handleClickText = (data: string) => {
  emit('click:text', data)
}

watch(
  config,
  async (value, prev) => {
    if (isStorageUdpated.value) {
      isStorageUdpated.value = false
    } else {
      const configs = cloneDeep(configsList.value)
      configs[configIndex.value] = value
      isStorageUdpated.value = true
      emit('update:storage', {
        configs,
        index: configIndex.value,
      })
    }
    if (!isConfigChanged(value, prev)) return
    loading.value = true
    fetchData(value)
  },
  { immediate: true, deep: true },
)
</script>
